import { create } from 'zustand';
import { booleanField } from '../lib/jsonBody';

const STORAGE_KEY = 'shot_case_battle_prefs';

export interface CaseBattlePrefs {
  reactions: boolean;
  affordableOnly: boolean;
  hideRunning: boolean;
}

interface CaseBattlePrefsState extends CaseBattlePrefs {
  hydrated: boolean;
  setPref: <K extends keyof CaseBattlePrefs>(key: K, value: CaseBattlePrefs[K]) => void;
  hydrate: () => void;
}

const DEFAULTS: CaseBattlePrefs = { reactions: true, affordableOnly: false, hideRunning: false };
const KEYS: (keyof CaseBattlePrefs)[] = ['reactions', 'affordableOnly', 'hideRunning'];

const read = (): Partial<CaseBattlePrefs> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    const prefs: Partial<CaseBattlePrefs> = {};
    KEYS.forEach((key) => {
      const value = booleanField(parsed, key);
      if (value !== undefined) prefs[key] = value;
    });
    return prefs;
  } catch {
    return {};
  }
};

const write = (prefs: CaseBattlePrefs) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
  }
};

const pick = (state: CaseBattlePrefsState): CaseBattlePrefs => ({
  reactions: state.reactions,
  affordableOnly: state.affordableOnly,
  hideRunning: state.hideRunning,
});

export const useCaseBattlePrefsStore = create<CaseBattlePrefsState>((set, get) => ({
  ...DEFAULTS,
  hydrated: false,
  setPref: (key, value) => {
    set({ [key]: value });
    write(pick(get()));
  },
  hydrate: () => {
    if (get().hydrated) return;
    set({ ...DEFAULTS, ...read(), hydrated: true });
  },
}));
